import { useEffect } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import { baseApi } from "@/services/api/baseApi";
import { subscribeToAuthChanges } from "@/features/auth/authSync";
import { clearLoggedOut, clearRedirectPath, getRedirectPath } from "@/features/auth/authStorage";
import { logOut, selectCurrentUser } from "@/features/auth/authSlice";
import { extractPermissions, resolveRedirectPath } from "@/features/auth/permissions";
import { applySession, loadSession, resetSession } from "@/features/auth/sessionBootstrap";
import { usesLocalJwtAuth } from "@/services/api/baseUrl";
import { useAuthStatusQuery } from "@/services/auth/authApi";

/**
 * Revalidation is throttled to this, because `focus` and `visibilitychange` both
 * fire when a tab comes back and a user alt-tabbing does not need a request per
 * switch.
 */
const REVALIDATE_MAX_AGE_MS = 60_000;

/**
 * How often the session is checked while the tab is open and focused.
 *
 * Events alone leave one hole: a tab that is simply left open and read does not
 * fire `focus` or `visibilitychange` and issues no requests, so a session
 * revoked server-side goes unnoticed until the user does something. The old app
 * covered this with a 30s `setInterval`. This is the same guarantee at a
 * quarter of the request rate, and RTK Query skips it entirely while the tab is
 * unfocused — the reason the old poll was wasteful was that it ran everywhere,
 * not that it existed.
 */
const SESSION_POLL_MS = 120_000;

/**
 * What is left of session lifecycle after the initial restore moved out.
 *
 * Restoring the session is not a subscription and no longer lives here — it runs
 * at module scope in `main.jsx`, before React mounts, which is what stopped `/me`
 * from firing once per mount. What remains genuinely is a subscription: other
 * tabs, and revalidation.
 *
 * Revalidation is driven by events instead of the old 30s poll. A timer on a
 * backgrounded tab spends requests to learn nothing, while returning to a tab
 * after an hour away is the moment staleness actually matters. A 401 on any
 * request is already turned into a logout by the base query's reauth handler, so
 * nothing has to poll to notice a dead session.
 */
export function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);

  // On localhost the bearer token is fixed, so there is no session to watch.
  const isDev = usesLocalJwtAuth();

  const { error: authStatusError } = useAuthStatusQuery(undefined, {
    skip: !user || isDev,
    pollingInterval: SESSION_POLL_MS,
    skipPollingIfUnfocused: true,
  });

  // A 401 here means the session died without the user making a request. Every
  // other 401 in the app is already handled by the base query's reauth, which
  // logs out once a refresh fails.
  useEffect(() => {
    if (isDev || !user) return;
    if (Number(authStatusError?.status) !== 401) return;

    resetSession();
    dispatch(baseApi.util.resetApiState());
    dispatch(logOut());
    navigate("/login", { replace: true });
  }, [authStatusError, dispatch, isDev, navigate, user]);

  // Mirror login/logout from other tabs.
  useEffect(
    () =>
      subscribeToAuthChanges((event) => {
        if (event === "loggedOut") {
          resetSession();
          dispatch(baseApi.util.resetApiState());
          dispatch(logOut());
          navigate("/login", { replace: true });
        } else if (event === "loggedIn") {
          clearLoggedOut();
          resetSession();
          loadSession().then((result) => {
            applySession(store, result);
            if (!result.user) return;

            const target = resolveRedirectPath(
              extractPermissions(result.user),
              getRedirectPath(),
            );
            clearRedirectPath();
            navigate(target, { replace: true });
          });
        }
      }),
    [dispatch, navigate, store],
  );

  // Revalidate when the user comes back to the tab, or the network returns.
  useEffect(() => {
    // On localhost the bearer token is fixed; there is no session to revalidate.
    if (isDev) return undefined;

    const revalidate = () => {
      if (document.visibilityState === "hidden") return;
      if (!store.getState().auth.user) return;
      loadSession({ maxAgeMs: REVALIDATE_MAX_AGE_MS }).then((result) =>
        applySession(store, result),
      );
    };

    document.addEventListener("visibilitychange", revalidate);
    window.addEventListener("focus", revalidate);
    window.addEventListener("online", revalidate);

    return () => {
      document.removeEventListener("visibilitychange", revalidate);
      window.removeEventListener("focus", revalidate);
      window.removeEventListener("online", revalidate);
    };
  }, [isDev, store]);

  return children;
}
